import React, { useMemo } from "react";
import { useI18n } from "@core/i18n/i18n";
import { getLowercaseCountryCodeFromIntlLocale } from "@core/i18n/locale-helper";

import styled from "@emotion/styled";
import { CountryFlag } from "@ui/components/common/country-flag";
import { isNotNil } from "@utils/assert";
import { countries } from "countries-list";
import { Dropdown, DropdownProps } from "primereact/dropdown";
import {
	CountryLanguageOptions,
	useCountryLanguageOptions,
} from "@ui/components/inputs/country-language-input/hooks/use-country-language-options";

interface CountryLanguageInputProps
	extends Omit<DropdownProps, "options" | "optionLabel" | "optionValue" | "valueTemplate" | "itemTemplate"> {
	error?: boolean;
	locales: Map<string, string[]>;
	forceLongName?: boolean;
}

export const CountryLanguageInput = ({
	error = false,
	locales,
	forceLongName = false,
	...otherProps
}: CountryLanguageInputProps): JSX.Element => {
	const { format } = useI18n();
	const countrylanguageOptions = useCountryLanguageOptions(locales, forceLongName, GET_COUNTRY_NAME);
	const options = useMemo(() => Array.from(countrylanguageOptions.keys()), [countrylanguageOptions]);

	return (
		<Dropdown
			{...otherProps}
			className={error ? "p-invalid" : undefined}
			options={options}
			emptyMessage={format("register.countrySelection.countryDropdown.emptyMessage")}
			valueTemplate={(locale: string | undefined) => CountryLanguageOptionTemplate(locale, countrylanguageOptions)}
			itemTemplate={(locale: string | undefined) => CountryLanguageOptionTemplate(locale, countrylanguageOptions)}
		/>
	);
};

export const GET_COUNTRY_NAME = (countryCode: string): string =>
	countries[countryCode.toUpperCase() as keyof typeof countries].native;

const CountryLanguageOptionTemplate = (
	locale: string | undefined,
	options: CountryLanguageOptions,
): JSX.Element => {
	const { format } = useI18n();

	if (isNotNil(locale)) {
		const countryCode = getLowercaseCountryCodeFromIntlLocale(locale);

		return (
			<DropdownItemContainer>
				<>
					<StyledCountryFlag code={countryCode.toUpperCase()} />
					<div>{options.get(locale)}</div>
				</>
			</DropdownItemContainer>
		);
	}

	return (
		<DropdownItemContainer>
			<p>{format("global.form.dropdown.placeholder")}</p>
		</DropdownItemContainer>
	);
};

const DropdownItemContainer = styled.div`
	display: flex;
	place-items: center;
	gap: 0.75em;
`;

const StyledCountryFlag = styled(CountryFlag)`
	height: 1em;
	width: 1.5em;
	border-radius: 2px;
`;
